import createAxiosInstance from "../utils/apiUtils";

// Use environment variable for Go backend base URL
const GO_BASE_URL = process.env.REACT_APP_GO_BASE_URL_DEV || process.env.REACT_APP_GO_BASE_URL || "http://localhost:8080";
const PLAID_BASE_URL = `${GO_BASE_URL}/plaid/`;
const GO_API_KEY = process.env.REACT_APP_GO_API_KEY || "aardvark5000";

// Only log in development
if (process.env.NODE_ENV === 'development') {
	console.log("Go Base URL:", GO_BASE_URL);
	console.log("Plaid API Base URL:", PLAID_BASE_URL);
	console.log("Go API Key:", GO_API_KEY);
	console.log("Full URL will be:", PLAID_BASE_URL + "identity-verification-complete");
}

const instance = createAxiosInstance(
	{
		baseURL: PLAID_BASE_URL,
		headers: {
			"X-API-Key": GO_API_KEY,
		},
	},
	false // Disable JWT interceptor for Go backend - uses X-API-Key instead
);

// Create separate instance for non-Plaid Go backend endpoints
const goInstance = createAxiosInstance(
	{
		baseURL: GO_BASE_URL,
		headers: {
			"X-API-Key": GO_API_KEY,
		},
	},
	false // Disable JWT interceptor for Go backend - uses X-API-Key instead
);

/**
 * Creates an identity verification session for KYC
 * @param {Object} data - Verification data
 * @param {string} data.dc_dealer_id - Dealer ID
 * @param {string} data.dc_contact_id - Contact ID
 * @param {string} data.user_id - User ID
 * @param {string} data.given_name - First name
 * @param {string} data.family_name - Last name
 * @param {string} data.email - Email address
 * @param {string} data.phone_number - Phone number
 * @param {string} data.date_of_birth - Date of birth (YYYY-MM-DD)
 * @param {Object} data.address - Address object
 * @param {string} data.address.street - Street address
 * @param {string} data.address.city - City
 * @param {string} data.address.region - State/Region
 * @param {string} data.address.postal_code - Postal code
 * @param {string} data.address.country - Country code (e.g., "US")
 * @returns {Promise} Response with shareable_url and verification_id
 * @note template_id is handled by the backend and does not need to be sent from frontend
 */
export const createIdentityVerification = async (data) => {
	if (process.env.NODE_ENV === 'development') {
		console.log("createIdentityVerification - Request URL:", `${PLAID_BASE_URL}identity-verification-complete`);
		console.log("createIdentityVerification - Request Headers:", instance.defaults.headers);
		console.log("createIdentityVerification - Request Data:", data);
	}
	return await instance.post("identity-verification-complete", data);
};

/**
 * Gets the status of an identity verification session
 * @param {string} verificationID - Plaid verification ID
 * @returns {Promise} Response with verification status and details
 */
export const getIdentityVerificationStatus = async (verificationID) => {
	return await instance.post("identity-verification-complete/status", {
		identity_verification_id: verificationID,
	});
};

/**
 * Gets the status of an identity verification session by dealer and contact IDs
 * @param {string} dcDealerID - DealerClick dealer ID (8-digit format)
 * @param {string} dcContactID - DealerClick contact ID
 * @returns {Promise} Response with verification status and details
 */
export const getIdentityVerificationStatusByContact = async (dcDealerID, dcContactID) => {
	return await instance.post("identity-verification-status", {
		dc_dealer_id: dcDealerID,
		dc_contact_id: dcContactID,
	});
};

/**
 * Creates a link token for Plaid Link initialization
 * @param {string} userID - User identifier
 * @param {string} userEmail - User email
 * @returns {Promise} Response with link_token
 */
export const createLinkToken = async (userID, userEmail) => {
	return await instance.post("create-link-token", {
		user_id: userID,
		user_email: userEmail,
	});
};

/**
 * Exchanges a public token for an access token
 * @param {string} publicToken - Public token from Plaid Link
 * @param {string} userID - User identifier
 * @returns {Promise} Response with access_token, item_id, and accounts
 */
export const exchangePublicToken = async (publicToken, userID) => {
	return await instance.post("exchange-public-token", {
		public_token: publicToken,
		user_id: userID,
	});
};

/**
 * Saves bank verification details to database
 * @param {Object} verificationData - Bank verification data
 * @param {string} verificationData.dc_dealer_id - Dealer ID
 * @param {string} verificationData.dc_contact_id - Contact ID
 * @param {string} verificationData.user_id - User ID
 * @param {string} verificationData.access_token - Plaid access token
 * @param {string} verificationData.item_id - Plaid item ID
 * @param {string} verificationData.account_id - Account ID
 * @param {string} verificationData.account_name - Account name
 * @param {string} verificationData.account_mask - Account mask (last 4 digits)
 * @param {string} verificationData.account_type - Account type (checking, savings, etc.)
 * @param {string} verificationData.institution_id - Institution ID
 * @returns {Promise} Response with saved verification record
 */
export const saveBankVerification = async (verificationData) => {
	return await instance.post("save-bank-verification", verificationData);
};

/**
 * Checks if contact has sufficient funds
 * @param {string} dcContactID - Contact ID
 * @param {number} amount - Amount to verify
 * @returns {Promise} Response with has_sufficient_funds boolean
 */
export const checkFundsByContact = async (dcContactID, amount) => {
	return await instance.post("check-funds-by-contact", {
		dc_contact_id: dcContactID,
		amount: amount,
	});
};

/**
 * Creates a dealership in the Plaid system
 * @param {Object} dealershipData - Dealership data from the store
 * @param {string} dealershipData.dealerID - Dealer ID
 * @param {string} dealershipData.locationID - Location ID
 * @param {string} dealershipData.dealerCompanyName - Dealership company name
 * @param {string} dealershipData.locationAddress - Location street address
 * @param {string} dealershipData.locationUnit - Location unit/suite
 * @param {string} dealershipData.locationCity - Location city
 * @param {string} dealershipData.locationState - Location state
 * @param {string} dealershipData.locationZipCode - Location zip code
 * @param {string} dealershipData.locationFirstName - Location contact first name
 * @param {string} dealershipData.locationLastName - Location contact last name
 * @param {string} dealershipData.locationEmail - Location contact email
 * @param {string} dealershipData.locationPhone - Location phone number
 * @param {string} dealershipData.dealerWebsite - Dealership website URL
 * @returns {Promise} Response with created dealership data
 */
export const createDealership = async (dealershipData) => {
	// Format dc_dealer_id as 8-digit: dealerID (4 digits) + locationID (4 digits)
	const dcDealerID = String(dealershipData.dealerID).padStart(4, "0") + String(dealershipData.locationID).padStart(4, "0");

	const data = {
		dc_dealer_id: dcDealerID,
		is_multi_loc: false,
		status: "Active",
		name: dealershipData.dealerCompanyName || "",
		address_line1: dealershipData.locationAddress || "",
		address_line2: dealershipData.locationUnit || "",
		city: dealershipData.locationCity || "",
		state_province: dealershipData.locationState || "",
		postal_code: dealershipData.locationZipCode || "",
		country: "US", // Default to US
		primary_contact_name: dealershipData.locationFirstName && dealershipData.locationLastName
			? `${dealershipData.locationFirstName} ${dealershipData.locationLastName}`.trim()
			: "",
		primary_contact_email: dealershipData.locationEmail || "",
		office_contact_phone: dealershipData.locationPhone || "",
		website_url: dealershipData.dealerWebsite || "",
	};

	return await goInstance.post("/dealerships", data);
};

/**
 * Gets account balance with historical averages for a contact
 * @param {string} dcDealerID - DealerClick dealer ID (8-digit format)
 * @param {string} dcContactID - DealerClick contact ID
 * @returns {Promise} Response with balance data including 30-day and 90-day averages
 */
export const getAccountBalance = async (dcDealerID, dcContactID) => {
	return await instance.post("get-account-balance", {
		dc_dealer_id: dcDealerID,
		dc_contact_id: dcContactID,
	});
};

/**
 * Gets income verification data for a contact
 * @param {string} dcDealerID - DealerClick dealer ID (8-digit format)
 * @param {string} dcContactID - DealerClick contact ID
 * @returns {Promise} Response with income verification data (bank or payroll)
 */
export const getIncomeVerification = async (dcDealerID, dcContactID) => {
	return await instance.post("income/get-verification", {
		dc_dealer_id: dcDealerID,
		dc_contact_id: dcContactID,
	});
};

/**
 * Creates a shareable bank linking URL for customer
 * @param {string} dcDealerID - DealerClick dealer ID (8-digit format)
 * @param {string} dcContactID - DealerClick contact ID
 * @returns {Promise} Response with shareable_url for customer to link bank account
 */
export const createShareableBankLink = async (dcDealerID, dcContactID) => {
	return await instance.post("create-shareable-bank-link", {
		dc_dealer_id: dcDealerID,
		dc_contact_id: dcContactID,
	});
};
